import { ProtocolVersionLatest, } from '@standardnotes/common';
import { CreateNewRootKey, CreateAnyKeyParams, SNRootKey, isErrorDecryptingParameters, } from '@standardnotes/encryption';
import { DecryptedPayload, EncryptedPayload, FillItemContentSpecialized, } from '@standardnotes/models';
import { StorageKey } from '../Storage/StorageKeys';
import { StorageValueModes } from '../Storage/StorageTypes';
import { EncryptTypeAPayload } from '../Encryption/UseCase/TypeA/EncryptPayload';
import { DecryptTypeAPayload } from '../Encryption/UseCase/TypeA/DecryptPayload';
import { AbstractService } from '../Service/AbstractService';
import { RootKeyManagerEvent } from './RootKeyManagerEvent';
import { KeyMode } from './KeyMode';
export class RootKeyManager extends AbstractService {
    constructor(device, storage, operators, identifier, _reencryptTypeAItems, eventBus) {
        super(eventBus);
        this.device = device;
        this.storage = storage;
        this.operators = operators;
        this.identifier = identifier;
        this._reencryptTypeAItems = _reencryptTypeAItems;
        this.keyMode = KeyMode.RootKeyNone;
    }
    deinit() {
        super.deinit();
        this.device = undefined;
        this.storage = undefined;
        this.operators = undefined;
        this.identifier = undefined;
        this._reencryptTypeAItems = undefined;
        this.rootKey = undefined;
        this.memoizedRootKeyParams = undefined;
    }
    async initialize() {
        const wrappedRootKey = this.getWrappedRootKey();
        const accountKeyParams = this.recomputeAccountKeyParams();
        const hasWrapper = await this.hasRootKeyWrapper();
        const hasRootKey = wrappedRootKey != undefined || accountKeyParams != undefined;
        if (hasWrapper && hasRootKey) {
            this.keyMode = KeyMode.RootKeyPlusWrapper;
        }
        else if (hasWrapper && !hasRootKey) {
            this.keyMode = KeyMode.WrapperOnly;
        }
        else if (!hasWrapper && hasRootKey) {
            this.keyMode = KeyMode.RootKeyOnly;
        }
        else if (!hasWrapper && !hasRootKey) {
            this.keyMode = KeyMode.RootKeyNone;
        }
        else {
            throw 'Invalid key mode condition';
        }
        if (this.keyMode === KeyMode.RootKeyOnly) {
            this.setRootKeyInstance(await this.getRootKeyFromKeychain());
            await this.handleKeyStatusChange();
        }
    }
    getMemoizedRootKeyParams() {
        return this.memoizedRootKeyParams;
    }
    getKeyMode() {
        return this.keyMode;
    }
    async hasRootKeyWrapper() {
        const wrapper = this.getRootKeyWrapperKeyParams();
        return wrapper != undefined;
    }
    getRootKeyWrapperKeyParams() {
        const rawKeyParams = this.storage.getValue(StorageKey.RootKeyWrapperKeyParams, StorageValueModes.Nonwrapped);
        if (!rawKeyParams) {
            return undefined;
        }
        return CreateAnyKeyParams(rawKeyParams);
    }
    async passcodeUpgradeAvailable() {
        const passcodeParams = this.getRootKeyWrapperKeyParams();
        if (!passcodeParams) {
            return false;
        }
        return passcodeParams.version !== ProtocolVersionLatest;
    }
    hasAccount() {
        switch (this.keyMode) {
            case KeyMode.RootKeyNone:
            case KeyMode.WrapperOnly:
                return false;
            case KeyMode.RootKeyOnly:
            case KeyMode.RootKeyPlusWrapper:
                return true;
            default:
                throw Error('Unhandled keyMode value.');
        }
    }
    getUserVersion() {
        const keyParams = this.memoizedRootKeyParams;
        return keyParams === null || keyParams === void 0 ? void 0 : keyParams.version;
    }
    hasRootKeyEncryptionSource() {
        return this.hasAccount() || this.hasPasscode();
    }
    async computeRootKey(password, keyParams) {
        const version = keyParams.version;
        const operator = this.operators.operatorForVersion(version);
        return operator.computeRootKey(password, keyParams);
    }
    /**
     * Deletes root key and wrapper from keychain. Used when signing out of application.
     */
    async deleteWorkspaceSpecificKeyStateFromDevice() {
        await this.device.clearNamespacedKeychainValue(this.identifier);
        await this.storage.removeValue(StorageKey.WrappedRootKey, StorageValueModes.Nonwrapped);
        await this.storage.removeValue(StorageKey.RootKeyWrapperKeyParams, StorageValueModes.Nonwrapped);
        await this.storage.removeValue(StorageKey.RootKeyParams, StorageValueModes.Nonwrapped);
        this.keyMode = KeyMode.RootKeyNone;
        this.setRootKeyInstance(undefined);
        await this.handleKeyStatusChange();
    }
    async createRootKey(identifier, password, origination, version) {
        const operator = version ? this.operators.operatorForVersion(version) : this.operators.defaultOperator();
        return operator.createRootKey(identifier, password, origination);
    }
    async validateAccountPassword(password) {
        const key = await this.computeRootKey(password, this.memoizedRootKeyParams);
        const valid = this.getSureRootKey().compare(key);
        if (valid) {
            return { valid, artifacts: { rootKey: key } };
        }
        else {
            return { valid: false };
        }
    }
    async validatePasscode(passcode) {
        const keyParams = this.getSureRootKeyWrapperKeyParams();
        const key = await this.computeRootKey(passcode, keyParams);
        const valid = await this.validateWrappingKey(key);
        if (valid) {
            return { valid, artifacts: { wrappingKey: key } };
        }
        else {
            return { valid: false };
        }
    }
    async getEncryptionSourceVersion() {
        if (this.hasAccount()) {
            return this.getSureUserVersion();
        }
        else if (this.hasPasscode()) {
            const passcodeParams = this.getSureRootKeyWrapperKeyParams();
            return passcodeParams.version;
        }
        throw Error('Attempting to access encryption source version without source');
    }
    getSureUserVersion() {
        const keyParams = this.memoizedRootKeyParams;
        return keyParams.version;
    }
    async handleKeyStatusChange() {
        this.recomputeAccountKeyParams();
        void this.notifyEvent(RootKeyManagerEvent.RootKeyManagerKeyStatusChanged);
    }
    hasPasscode() {
        return this.keyMode === KeyMode.WrapperOnly || this.keyMode === KeyMode.RootKeyPlusWrapper;
    }
    recomputeAccountKeyParams() {
        const rawKeyParams = this.storage.getValue(StorageKey.RootKeyParams, StorageValueModes.Nonwrapped);
        if (!rawKeyParams) {
            return;
        }
        this.memoizedRootKeyParams = CreateAnyKeyParams(rawKeyParams);
        return this.memoizedRootKeyParams;
    }
    getSureRootKeyWrapperKeyParams() {
        return this.getRootKeyWrapperKeyParams();
    }
    /**
     * Wraps the current in-memory root key value using the wrappingKey,
     * then persists the wrapped value to disk.
     */
    async wrapAndPersistRootKey(wrappingKey) {
        const rootKey = this.getSureRootKey();
        const value = {
            ...rootKey.payload.ejected(),
            content: FillItemContentSpecialized(rootKey.persistableValueWhenWrapping()),
        };
        const payload = new DecryptedPayload(value);
        const usecase = new EncryptTypeAPayload(this.operators);
        const wrappedKey = await usecase.executeOne(payload, wrappingKey);
        const wrappedKeyPayload = new EncryptedPayload({
            ...payload.ejected(),
            ...wrappedKey,
            errorDecrypting: false,
            waitingForKey: false,
        });
        this.storage.setValue(StorageKey.WrappedRootKey, wrappedKeyPayload.ejected(), StorageValueModes.Nonwrapped);
    }
    async unwrapRootKey(wrappingKey) {
        if (this.keyMode === KeyMode.WrapperOnly) {
            this.setRootKeyInstance(wrappingKey);
            return;
        }
        if (this.keyMode !== KeyMode.RootKeyPlusWrapper) {
            throw 'Invalid key mode condition for unwrapping.';
        }
        const wrappedKey = this.getWrappedRootKey();
        const payload = new EncryptedPayload(wrappedKey);
        const usecase = new DecryptTypeAPayload(this.operators);
        const decrypted = await usecase.executeOne(payload, wrappingKey);
        if (isErrorDecryptingParameters(decrypted)) {
            throw Error('Unable to decrypt root key with provided wrapping key.');
        }
        else {
            const decryptedPayload = new DecryptedPayload({
                ...payload.ejected(),
                ...decrypted,
            });
            this.setRootKeyInstance(new SNRootKey(decryptedPayload));
            await this.handleKeyStatusChange();
        }
    }
    /**
     * Encrypts rootKey and saves it in storage instead of keychain, and then
     * clears keychain. This is because we don't want to store large encrypted
     * payloads in the keychain. If the root key is not wrapped, it is stored
     * in plain form in the user's secure keychain.
     */
    async setNewRootKeyWrapper(wrappingKey) {
        if (this.keyMode === KeyMode.RootKeyNone) {
            this.keyMode = KeyMode.WrapperOnly;
        }
        else if (this.keyMode === KeyMode.RootKeyOnly) {
            this.keyMode = KeyMode.RootKeyPlusWrapper;
        }
        else {
            throw Error('Attempting to set wrapper on already wrapped key.');
        }
        await this.device.clearNamespacedKeychainValue(this.identifier);
        if (this.keyMode === KeyMode.WrapperOnly || this.keyMode === KeyMode.RootKeyPlusWrapper) {
            if (this.keyMode === KeyMode.WrapperOnly) {
                this.setRootKeyInstance(wrappingKey);
                await this._reencryptTypeAItems.execute();
            }
            else {
                await this.wrapAndPersistRootKey(wrappingKey);
            }
            this.storage.setValue(StorageKey.RootKeyWrapperKeyParams, wrappingKey.keyParams.getPortableValue(), StorageValueModes.Nonwrapped);
            await this.handleKeyStatusChange();
        }
        else {
            throw Error('Invalid keyMode on setNewRootKeyWrapper');
        }
    }
    /**
     * Removes root key wrapper from local storage and stores root key bare in secure keychain.
     */
    async removeRootKeyWrapper() {
        if (this.keyMode !== KeyMode.WrapperOnly && this.keyMode !== KeyMode.RootKeyPlusWrapper) {
            throw Error('Attempting to remove root key wrapper on unwrapped key.');
        }
        if (this.keyMode === KeyMode.WrapperOnly) {
            this.keyMode = KeyMode.RootKeyNone;
            this.setRootKeyInstance(undefined);
        }
        else if (this.keyMode === KeyMode.RootKeyPlusWrapper) {
            this.keyMode = KeyMode.RootKeyOnly;
        }
        await this.storage.removeValue(StorageKey.WrappedRootKey, StorageValueModes.Nonwrapped);
        await this.storage.removeValue(StorageKey.RootKeyWrapperKeyParams, StorageValueModes.Nonwrapped);
        if (this.keyMode === KeyMode.RootKeyOnly) {
            await this.saveRootKeyToKeychain();
        }
        await this.handleKeyStatusChange();
    }
    async setRootKey(key, wrappingKey) {
        if (!key.keyParams) {
            throw Error('keyParams must be supplied if setting root key.');
        }
        if (this.getRootKey() === key) {
            throw Error('Attempting to set root key as same current value.');
        }
        if (this.keyMode === KeyMode.WrapperOnly) {
            this.keyMode = KeyMode.RootKeyPlusWrapper;
        }
        else if (this.keyMode === KeyMode.RootKeyNone) {
            this.keyMode = KeyMode.RootKeyOnly;
        }
        else if (this.keyMode === KeyMode.RootKeyOnly || this.keyMode === KeyMode.RootKeyPlusWrapper) {
            /** Root key is simply changing, mode stays the same */
            /** this.keyMode = this.keyMode; */
        }
        else {
            throw Error('Unhandled key mode for setNewRootKey');
        }
        this.setRootKeyInstance(key);
        this.storage.setValue(StorageKey.RootKeyParams, key.keyParams.getPortableValue(), StorageValueModes.Nonwrapped);
        if (this.keyMode === KeyMode.RootKeyOnly) {
            await this.saveRootKeyToKeychain();
        }
        else if (this.keyMode === KeyMode.RootKeyPlusWrapper) {
            if (!wrappingKey) {
                throw Error('wrappingKey must be supplied');
            }
            await this.wrapAndPersistRootKey(wrappingKey);
        }
        await this.handleKeyStatusChange();
    }
    getRootKeyParams() {
        if (this.keyMode === KeyMode.WrapperOnly) {
            return this.getRootKeyWrapperKeyParams();
        }
        else if (this.keyMode === KeyMode.RootKeyOnly || this.keyMode === KeyMode.RootKeyPlusWrapper) {
            return this.recomputeAccountKeyParams();
        }
        else if (this.keyMode === KeyMode.RootKeyNone) {
            return undefined;
        }
        else {
            throw 'Unhandled key mode for getRootKeyParams';
        }
    }
    getSureRootKeyParams() {
        return this.getRootKeyParams();
    }
    async saveRootKeyToKeychain() {
        if (this.getRootKey() == undefined) {
            throw 'Attempting to non-existent root key to the keychain.';
        }
        if (this.keyMode !== KeyMode.RootKeyOnly) {
            throw 'Should not be persisting wrapped key to keychain.';
        }
        const rawKey = this.getSureRootKey().getKeychainValue();
        return this.executeCriticalFunction(() => {
            return this.device.setNamespacedKeychainValue(rawKey, this.identifier);
        });
    }
    /**
     * We know a wrappingKey is correct if it correctly decrypts
     * wrapped root key.
     */
    async validateWrappingKey(wrappingKey) {
        const wrappedRootKey = this.getWrappedRootKey();
        /** If wrapper only, storage is encrypted directly with wrappingKey */
        if (this.keyMode === KeyMode.WrapperOnly) {
            return this.storage.canDecryptWithKey(wrappingKey);
        }
        else if (this.keyMode === KeyMode.RootKeyOnly || this.keyMode === KeyMode.RootKeyPlusWrapper) {
            /**
             * In these modes, storage is encrypted with account keys, and
             * account keys are encrypted with wrappingKey. Here we validate
             * by attempting to decrypt account keys.
             */
            const wrappedKeyPayload = new EncryptedPayload(wrappedRootKey);
            const usecase = new DecryptTypeAPayload(this.operators);
            const decrypted = await usecase.executeOne(wrappedKeyPayload, wrappingKey);
            return !isErrorDecryptingParameters(decrypted);
        }
        else {
            throw 'Unhandled case in validateWrappingKey';
        }
    }
    getWrappedRootKey() {
        return this.storage.getValue(StorageKey.WrappedRootKey, StorageValueModes.Nonwrapped);
    }
    setRootKeyInstance(rootKey) {
        this.rootKey = rootKey;
    }
    getRootKey() {
        return this.rootKey;
    }
    getSureRootKey() {
        return this.rootKey;
    }
    async getRootKeyFromKeychain() {
        const rawKey = (await this.device.getNamespacedKeychainValue(this.identifier));
        if (rawKey == undefined) {
            return undefined;
        }
        const keyParams = this.getSureRootKeyParams();
        return CreateNewRootKey({
            ...rawKey,
            keyParams: keyParams.getPortableValue(),
        });
    }
}
